import React, { forwardRef, useImperativeHandle, useRef } from 'react'

import { HiMagnifyingGlass, HiOutlineXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

interface SearchBar extends React.HTMLProps<HTMLInputElement> {
  containerClassName?: string
  iconClassName?: string
  iconContainerClassName?: string
  rightAdornment?: React.ReactNode
  setValue?: (value: string) => void
}

const SearchBar = forwardRef<HTMLInputElement, SearchBar>(
  (
    {
      onKeyDown,
      iconClassName,
      iconContainerClassName,
      containerClassName,
      rightAdornment,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => inputRef.current!)

    const handleClear = () => {
      if (props.setValue) {
        props.setValue('')
      }
    }

    const hasClearButton = Boolean(
      props.value !== '' && props.value !== undefined && props.setValue,
    )
    const hasRightAdornment = Boolean(rightAdornment)

    return (
      <div className={twMerge('relative rounded-md', containerClassName ?? '')}>
        <div
          className={twMerge(
            'pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5',
            iconContainerClassName ?? '',
          )}
        >
          <HiMagnifyingGlass
            className={twMerge(
              'h-5 w-5 text-primary-500 dark:text-primary-500',
              iconClassName ?? '',
            )}
            aria-hidden="true"
          />
        </div>
        <input
          ref={inputRef}
          type={props.type}
          name={props.name}
          id={props.id}
          className={twMerge(
            'block w-full rounded-md border-0 h-[34px] pl-9 text-primary-900 dark:text-primary-300',
            'placeholder:text-primary-400 dark:placeholder:text-primary-500',
            'border-[1px] border-primary-300 hover:border-primary-400 focus:border-primary-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6',
            'dark:bg-primary-900 dark:border-primary-700 dark:hover:border-primary-600 dark:focus:border-primary-500',
            'transition duration-100',
            hasClearButton && hasRightAdornment
              ? 'pr-14'
              : hasClearButton || hasRightAdornment
                ? 'pr-8'
                : 'pr-2.5',
            props.className ?? '',
          )}
          placeholder={props.placeholder}
          value={props.value}
          onChange={props.onChange}
          onKeyDown={onKeyDown}
          onFocus={props.onFocus}
        />
        {hasRightAdornment && (
          <div
            className={twMerge(
              'absolute inset-y-0 flex items-center',
              hasClearButton ? 'right-8' : 'right-2',
            )}
          >
            {rightAdornment}
          </div>
        )}
        {hasClearButton && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center pr-2 hover:opacity-70"
          >
            <HiOutlineXMark
              className="h-4 w-4 text-primary-500 dark:text-primary-500"
              aria-hidden="true"
            />
          </button>
        )}
      </div>
    )
  },
)

SearchBar.displayName = '@sweetspot/components/common/SearchBar'

export default SearchBar
